import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../components/Button';
import QuizCard from '../components/QuizCard';
import QuizModal from '../components/QuizModal';
import { LoadingOverlaySkeleton } from '../components/SkeletonLoader';
import { QuizSkeleton } from '../components/SkeletonLoader';
import { useAuth } from '../hooks/useAuth';
import apiService from '../services/api';

const Quiz = () => {
  const { subject } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    loadQuizzes();
  }, [subject]);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      // Load existing quizzes for this subject
      const response = await apiService.getExistingContent('quiz', subject, '', 1, 50);
      setQuizzes(response.content || []);
    } catch (error) {
      console.error('Error loading quizzes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuiz = async () => {
    try {
      setGenerating(true);
      const response = await apiService.generateSubjectQuiz(subject, 25);
      setSelectedQuiz(response);
      setShowQuizModal(true);
    } catch (error) {
      console.error('Error generating quiz:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleQuizClick = (quiz) => {
    setSelectedQuiz(quiz);
    setShowQuizModal(true);
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return <QuizSkeleton />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/60 dark:bg-gray-900/60 backdrop-blur-lg rounded-2xl shadow-lg p-6 mb-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleBack}
                  className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  ← Back
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                    {subject} Quiz Center
                  </h1>
                  <p className="text-gray-600 dark:text-gray-400">
                    Test your knowledge with interactive quizzes
                  </p>
                </div>
              </div>
              <Button
                onClick={handleGenerateQuiz}
                disabled={generating}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-xl font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200"
              >
                {generating ? 'Generating...' : 'Generate New Quiz'}
              </Button>
            </div>
          </motion.div>

          {/* Quiz Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {quizzes.map((quiz, index) => (
              <QuizCard
                key={quiz.id || index}
                quiz={quiz}
                onClick={() => handleQuizClick(quiz)}
              />
            ))}
          </motion.div>

          {quizzes.length === 0 && !loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <div className="text-gray-500 dark:text-gray-400 text-lg">
                No quizzes available yet. Generate your first quiz!
              </div>
            </motion.div>
          )}
        </div>
      </div>

      {/* Quiz Modal */}
      {selectedQuiz && (
        <QuizModal
          isOpen={showQuizModal}
          onClose={() => setShowQuizModal(false)}
          subject={subject}
          quiz={selectedQuiz}
        />
      )}
    </div>
  );
};

export default Quiz; 